import React, { useCallback, useMemo, useState } from 'react';

import { Link } from 'gatsby';

import { Button, Grid2 as Grid, styled, useMediaQuery } from '@mui/material';

import {
  ContactTileTeaser,
  LogoEmbroideryServiceIcon,
  ProductTile,
  ServiceTileTeaser,
} from '@mewa/ui';

import t from '../../../libs/translate';
import Image from '../../components/image';
import { ProductFallbackImage } from '../../components/product-fallback-image';
import { ProductCardBaseData } from '../../components/slider/product-card-base';
import { jpgPath } from '../../helpers/asset-factory';
import { CollectionProducts } from '../../helpers/product-grouping';
import { useShowMoreItems } from '../../helpers/use-show-more-items';

export const IndividualProducts = ({
  collections,
  langPrefix,
  translations,
  conversionLink,
  onContactTeaserClick,
  hideTeaser = false,
  isNonClothing = false,
}: {
  collections: CollectionProducts<ProductCardBaseData & { hide?: boolean }>[];
  langPrefix: string;
  translations: Record<string, string>;
  conversionLink: string;
  onContactTeaserClick: () => void;
  hideTeaser?: boolean;
  isNonClothing?: boolean;
}) => {
  const [selected, setSelected] = useState<number[]>([]);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery((theme) =>
    theme.breakpoints.between('sm', 'lg'),
  );

  const products = useMemo(
    () =>
      collections.flatMap((collection) =>
        collection.products.map((product) => ({
          collection,
          product: {
            ...product,
            variantsCountText: t(
              translations,
              'product_details_variants_count',
              {
                variantsCount: product.variants.length.toString(),
              },
            ),
            variants: product.variants.map((variant) => ({
              ...variant,
              image: variant.assetId ? (
                <Image
                  images={[
                    {
                      src: jpgPath(variant.assetId ?? '', 500 * 1.5, 500 * 1.5),
                      height: 500,
                      width: 500,
                      media: '',
                    },
                  ]}
                  alt=""
                />
              ) : (
                <StyledProductFallbackImage
                  useMbsFallbackIcon={!isNonClothing}
                />
              ),
            })),
          },
        })),
      ),
    [collections, isNonClothing, translations],
  );

  const hideContactTeaser = products.length < 9;
  const logoEmbroideryTeaserIndex =
    products.length < 9 ? 5 : ((products.length / 3) | 0) + 1;
  const contactTeaserIndex = (((products.length / 3) * 2) | 0) + 1;

  const getRealProductCountWithTeaser = (count: number, isMobile = false) => {
    if (!hideTeaser) {
      if (logoEmbroideryTeaserIndex <= count) {
        count -= isMobile ? 2 : 1;
      }

      if (!hideContactTeaser && contactTeaserIndex <= count) {
        count -= isMobile ? 2 : 1;
      }
    }

    return count;
  };

  const { itemCount, loadMoreItems } = useShowMoreItems({
    initialCount: getRealProductCountWithTeaser(
      isMobile ? 8 : isTablet ? 12 : 16,
      isMobile,
    ),
    showMoreCount: 999,
  });

  const select = useCallback((index: number) => {
    setSelected((old) => [...old, index]);
  }, []);

  const deselect = useCallback((index: number) => {
    setSelected((old) => [...old.filter((i) => i !== index)]);
  }, []);

  const logoEmbroideryTeaser = (
    <Grid size={{ xs: 12, sm: 4, lg: 3 }}>
      <ServiceTileTeaser
        headline={t(translations, 'product_details_logo_teaser_headline')}
        subline={t(translations, 'logo_embroidery_teaser_subline')}
        text={t(translations, 'logo_embroidery_teaser_description')}
        icon={
          <LogoEmbroideryServiceIcon
            width={128}
            height={128}
            fill="currentColor"
          />
        }
      />
    </Grid>
  );

  return (
    <Grid
      container
      columns={12}
      columnGap="var(--gutter)"
      rowGap="var(--spacer-l)"
      sx={{ alignItems: 'stretch', zIndex: -1 }}
    >
      {products.slice(0, itemCount).map(({ collection, product }, index) => {
        return (
          <React.Fragment key={product.uuid}>
            {!hideTeaser ? (
              <>
                {index + 1 === logoEmbroideryTeaserIndex
                  ? logoEmbroideryTeaser
                  : null}
                {index + 1 === contactTeaserIndex && !hideContactTeaser ? (
                  <Grid size={{ xs: 12, sm: 4, lg: 3 }}>
                    <ContactTileTeaser
                      headline={t(
                        translations,
                        'service_segment_conversion_teaser_headline',
                      )}
                      subline={t(
                        translations,
                        'service_segment_conversion_teaser_subline',
                      )}
                      link={{
                        component: Link,
                        label: t(translations, 'contact_teaser_headline'),
                        props: {
                          to: conversionLink,
                          onClick: onContactTeaserClick,
                        },
                      }}
                    />
                  </Grid>
                ) : null}
              </>
            ) : null}
            <ProductTile
              hideOnMobile={index % 2 === 0 && selected.includes(index + 1)}
              index={index}
              onSelect={select}
              onDeselect={deselect}
              detailsPageLabel={t(translations, 'show_product_details_label')}
              variantsCountText={product.variantsCountText}
              WrapperComponent={WrapperComponent}
              collectionName={isNonClothing ? '' : (collection.name ?? '')}
              title={isNonClothing ? product.modelName : product.displayName}
              variants={product.variants}
              langPrefix={langPrefix}
              slug={product.slug}
              buttonComponent={Link}
              isNonClothing={isNonClothing}
              keyFacts={product.keyFacts}
            />
          </React.Fragment>
        );
      })}

      {!hideTeaser && logoEmbroideryTeaserIndex > products.length
        ? logoEmbroideryTeaser
        : null}
      {products.length > 3 && itemCount < products.length ? (
        <Grid size={12}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 'var(--spacer-xl)',
            }}
          >
            <Button onClick={loadMoreItems}>
              {t(
                translations,
                'service_segment_individual_products_show_more',
                { itemsCount: products.length.toString() },
              )}
            </Button>
          </div>
        </Grid>
      ) : null}
    </Grid>
  );
};

const WrapperComponent = ({
  children,
  isExpanded,
  hideOnMobile = false,
  isNonClothing = false,
}: React.PropsWithChildren<{
  isExpanded: boolean;
  hideOnMobile?: boolean;
  isNonClothing?: boolean;
}>) => (
  <StyledWrapperComponent
    size={{ xs: isExpanded || isNonClothing ? 12 : 6, sm: 4, lg: 3 }}
    hideOnMobile={hideOnMobile}
  >
    {children}
  </StyledWrapperComponent>
);

const StyledWrapperComponent = styled(Grid, {
  shouldForwardProp: (props) => props !== 'hideOnMobile',
})<{ hideOnMobile?: boolean }>(({ hideOnMobile, theme }) => ({
  position: 'relative',

  [theme.breakpoints.down('sm')]: {
    display: hideOnMobile ? 'none' : ' block',
  },
}));

const StyledProductFallbackImage = styled(ProductFallbackImage)(
  ({ theme }) => ({
    width: '500px',
    height: '500px',

    maxWidth: '100%',
    maxHeight: '100%',
  }),
);
